import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  CtaButton,
  ModalContainer,
  SelectInput,
  TextInput,
} from '@tassomutuo-backoffice/ui';

import {
  correctManagedLeadRequestValues,
  ManagedLeadDetail,
  ManagedLeadProductPath,
  ManagedLeadRequestValuesPayload,
} from '../../../services/api/managed-leads/managedLeads';
import { managedLeadCurrencyInput } from '../managedLeadCurrency';
import { createManagedLeadIdempotencyKey } from '../managedLeadIdempotency';
import {
  managedLeadDetailQueryKey,
  managedLeadListQueryKey,
  managedLeadNotesQueryKey,
} from '../managedLeadQueryKeys';
import {
  hasManagedLeadRequestValuesEdits,
  initialManagedLeadRequestValues,
  managedLeadCorrectableValueLabels,
  managedLeadCorrectableValues,
  managedLeadRequestValuesDelta,
  managedLeadRequestValuesPayload,
  MANAGED_LEAD_REQUEST_VALUES_TITLE,
  ManagedLeadRequestValues,
  ManagedLeadRequestValuesErrors,
  validateManagedLeadRequestValues,
} from '../managedLeadRequestValues';
import {
  describeManagedLeadRequestValuesFailure,
  describeManagedLeadRequestValuesOutcome,
  MANAGED_LEAD_REQUEST_VALUES_UNCHANGED_MESSAGE,
} from '../managedLeadRequestValuesFeedback';
import ManagedLeadFailureAlert from './ManagedLeadFailureAlert';

/**
 * One submission: the payload and the key minted for it. The server shares one
 * idempotency register between the correction and the advancement update, so a
 * correction cannot be sent without a key of its own.
 */
interface RequestValuesAttempt {
  payload: ManagedLeadRequestValuesPayload;
  key: string;
}

interface ManagedLeadRequestValuesModalProps {
  open: boolean;
  handleClose: () => void;
  product: ManagedLeadProductPath;
  dealId: string;
  detail: ManagedLeadDetail;
}

const REFRESH_FAILED_MESSAGE =
  'Correzione salvata, ma non è stato possibile ricaricare i dati. Ricarica la pagina per vedere le informazioni aggiornate.';

/**
 * "Correggi i valori richiesti": the second write path of a managed lead, and
 * not a shortcut into the update form. It fixes what the customer stated at
 * acquisition, so it moves no stage and asks for no Nota - the server writes an
 * automatic one naming the old and the new figure, which is the only place the
 * previous value survives.
 */
const ManagedLeadRequestValuesModal = ({
  open,
  handleClose,
  product,
  dealId,
  detail,
}: ManagedLeadRequestValuesModalProps) => {
  const queryClient = useQueryClient();
  const contentRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<ManagedLeadRequestValues>({});
  const [errors, setErrors] = useState<ManagedLeadRequestValuesErrors>({});
  const [outstandingOutcome, setOutstandingOutcome] = useState<{
    message: string;
    retryable: boolean;
  } | null>(null);
  const [formFrozen, setFormFrozen] = useState(false);
  /**
   * The persisted figures the fields on screen were initialized from. When a
   * refetch brings new ones the form starts again from the backend, because
   * what the operator is correcting is what HubSpot holds and not what it held
   * when the page was opened.
   */
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  /**
   * The submission on screen. Riprova re-sends it verbatim instead of building
   * it again from the form: after a partial success the figures have already
   * been written, and only the request the server is holding open under that
   * key can resume the Nota alone.
   */
  const attemptRef = useRef<RequestValuesAttempt | null>(null);
  const inFlightRef = useRef(false);

  const fields = managedLeadCorrectableValues(product);
  const initial = initialManagedLeadRequestValues(product, detail);
  /**
   * The form belongs to one opening of the modal and to one set of persisted
   * figures: it starts again from the backend when the operator opens it, so an
   * edit abandoned earlier is never what the pencil opens on, and again when a
   * refetch brings figures other than the ones on screen.
   */
  const formKey = `${open}|${JSON.stringify(initial)}`;
  if (initializedFor !== formKey && !formFrozen && !outstandingOutcome) {
    // Adjusted while rendering, not in an effect: the fields must never paint
    // with values read from a detail the page has already replaced.
    setInitializedFor(formKey);
    setValues(initial);
    setErrors({});
  }

  const mutation = useMutation({
    mutationFn: (attempt: RequestValuesAttempt) =>
      correctManagedLeadRequestValues(
        product,
        dealId,
        attempt.payload,
        attempt.key
      ),
    onSuccess: (result) => {
      // The corrected amount is on the list card as well as on the detail, and
      // the write is applied and irreversible even when the Nota failed. A
      // correction the server found nothing to write for is re-read too: the
      // figures reaching it were a delta against the page, so it is the page
      // that is describing a lead somebody else has already corrected.
      const refreshes = [
        queryClient.invalidateQueries(
          { queryKey: managedLeadDetailQueryKey(product, dealId) },
          { throwOnError: true }
        ),
        queryClient.invalidateQueries(
          { queryKey: managedLeadListQueryKey(product) },
          { throwOnError: true }
        ),
      ];
      // The Notes are only stale once the automatic Nota exists.
      if (result.note.created) {
        refreshes.push(
          queryClient.invalidateQueries(
            { queryKey: managedLeadNotesQueryKey(product, dealId) },
            { throwOnError: true }
          )
        );
      }
      void Promise.all(refreshes).catch(() =>
        toast.warning(REFRESH_FAILED_MESSAGE)
      );

      // A partial success keeps the attempt: only the Nota is left, and the
      // modal stays open to say so instead of claiming a finished correction.
      if (result.outcome === 'partial_success') {
        const outcome = describeManagedLeadRequestValuesOutcome(result);
        setOutstandingOutcome({
          message: outcome.message,
          retryable: outcome.retryable,
        });
        return;
      }

      attemptRef.current = null;
      setOutstandingOutcome(null);
      toast.success(describeManagedLeadRequestValuesOutcome(result).message);
      handleClose();
    },
    onError: (error) => {
      const failure = describeManagedLeadRequestValuesFailure(error);
      // The key is burnt, but the attempt still holds the payload a retry has
      // to carry, so it is re-keyed instead of thrown away.
      if (failure.renewsIdempotencyKey && attemptRef.current) {
        attemptRef.current = {
          ...attemptRef.current,
          key: createManagedLeadIdempotencyKey(),
        };
      }
      if (failure.refetchesDetail) {
        setFormFrozen(true);
        queryClient.invalidateQueries({
          queryKey: managedLeadDetailQueryKey(product, dealId),
        });
      }
    },
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  const send = (attempt: RequestValuesAttempt, keepOutstanding = false) => {
    attemptRef.current = attempt;
    if (!keepOutstanding) setOutstandingOutcome(null);
    inFlightRef.current = true;
    mutation.mutate(attempt);
  };

  const changeValue = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors(({ [key]: _corrected, ...rest }) => rest);
    // Only a failure is cleared by typing: an outstanding Nota is not answered
    // by editing a figure, and its Riprova has to stay on screen.
    if (mutation.isError) mutation.reset();
  };

  const submit = () => {
    // A ref, not `isPending`: two clicks in the same tick both read the state
    // before React re-renders, and the second one must not reach the network.
    if (inFlightRef.current) return;

    const fieldErrors = validateManagedLeadRequestValues(
      product,
      detail,
      values
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) return;

    // Nothing differs from what HubSpot holds. Sending it would ask the server
    // for a correction that corrects nothing, and reporting it as saved would
    // tell the operator a figure changed when none did.
    if (!managedLeadRequestValuesDelta(product, detail, values).length) {
      toast.info(MANAGED_LEAD_REQUEST_VALUES_UNCHANGED_MESSAGE);
      handleClose();
      return;
    }

    const payload: ManagedLeadRequestValuesPayload = {
      values: managedLeadRequestValuesPayload(product, values),
    };
    const previous = attemptRef.current;
    // The same request keeps its key so the server can replay it or resume its
    // Nota instead of writing twice; a changed one is a new attempt and needs a
    // new key. Key order is this module's, so two identical sets of figures
    // always serialize the same way.
    const unchanged =
      previous && JSON.stringify(previous.payload) === JSON.stringify(payload);

    send(
      unchanged ? previous : { payload, key: createManagedLeadIdempotencyKey() }
    );
  };

  const retry = () => {
    if (inFlightRef.current || !attemptRef.current) return;
    send(attemptRef.current, true);
  };

  const failure = mutation.isError
    ? describeManagedLeadRequestValuesFailure(
        mutation.error,
        managedLeadCorrectableValueLabels(product)
      )
    : null;
  // What the operator typed comes first: a message the server wrote about the
  // previous value would otherwise sit under a field already corrected.
  const fieldMessages = { ...(failure?.fieldMessages ?? {}), ...errors };
  const fieldMessageSignature = Object.keys(fieldMessages).join('|');
  const dirty = Boolean(
    !outstandingOutcome && hasManagedLeadRequestValuesEdits(initial, values)
  );

  const requestClose = () => {
    if (mutation.isPending) return;
    if (
      dirty &&
      !window.confirm(
        'Vuoi uscire senza salvare? Le modifiche inserite andranno perse.'
      )
    ) {
      return;
    }
    if (!outstandingOutcome) {
      attemptRef.current = null;
      setErrors({});
      setFormFrozen(false);
      setInitializedFor(null);
      mutation.reset();
    }
    handleClose();
  };

  useEffect(() => {
    if (!fieldMessageSignature) return;
    const frame = window.requestAnimationFrame(() => {
      const invalid = contentRef.current?.querySelector<HTMLElement>(
        '[aria-invalid="true"]'
      );
      invalid?.scrollIntoView?.({ block: 'center' });
      invalid?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [fieldMessageSignature]);

  return (
    <ModalContainer
      open={open}
      handleClose={requestClose}
      closeDisabled={mutation.isPending}
      disableBackdropClose={dirty}
      stickyFooter
      title={MANAGED_LEAD_REQUEST_VALUES_TITLE}
      containerSx={{
        maxWidth: '500px',
        maxHeight: {
          xs: 'calc(100dvh - 16px)',
          sm: 'calc(100dvh - 32px)',
        },
      }}
      mtHeader={0}
    >
      <Box ref={contentRef} width="100%" maxWidth="450px">
        <Stack spacing={2}>
          {outstandingOutcome ? (
            <Alert
              severity="warning"
              role="status"
              action={
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {outstandingOutcome.retryable && (
                    <Button
                      color="inherit"
                      onClick={retry}
                      disabled={mutation.isPending}
                    >
                      {mutation.isPending
                        ? 'Creazione Nota…'
                        : 'Riprova creazione Nota'}
                    </Button>
                  )}
                  <Button
                    color="inherit"
                    onClick={requestClose}
                    disabled={mutation.isPending}
                  >
                    Chiudi
                  </Button>
                </Stack>
              }
            >
              {outstandingOutcome.message}
            </Alert>
          ) : (
            <Box
              component="form"
              noValidate
              aria-busy={mutation.isPending}
              sx={
                mutation.isPending || formFrozen
                  ? { pointerEvents: 'none', opacity: 0.72 }
                  : undefined
              }
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              {fields.map((field) => {
                const value = values[field.key] ?? '';
                const helperText = fieldMessages[field.key] ?? null;
                const error = Boolean(helperText);
                // Keyed to what HubSpot holds, not to what is selected: a stored
                // value outside the vocabulary has to stay on the menu, because
                // choosing it again is how the operator leaves the figure alone.
                const options = field.editor.options?.(
                  initial[field.key] ?? ''
                );

                // A figure with a closed vocabulary is chosen, never typed, so
                // the operator cannot enter a value the pipeline does not know.
                return options ? (
                  <SelectInput
                    key={field.key}
                    label={field.label}
                    disabled={mutation.isPending || formFrozen}
                    value={value}
                    onChange={(event) =>
                      changeValue(field.key, event.target.value)
                    }
                    selectOptions={options.map((option) => ({
                      value: option,
                      label: option,
                    }))}
                    error={error}
                    helperText={helperText}
                  />
                ) : (
                  <TextInput
                    key={field.key}
                    type="text"
                    label={field.label}
                    disabled={mutation.isPending || formFrozen}
                    showEuroIcon
                    value={value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      changeValue(
                        field.key,
                        managedLeadCurrencyInput(event.target.value)
                      )
                    }
                    error={error}
                    helperText={helperText}
                  />
                );
              })}

              <Box
                data-modal-footer
                display="flex"
                justifyContent="center"
                gap={2}
                mt={4}
              >
                <Button
                  type="button"
                  onClick={requestClose}
                  disabled={mutation.isPending}
                >
                  Annulla
                </Button>
                <CtaButton
                  type="submit"
                  variant="contained"
                  // Nothing has been touched yet, so there is nothing to correct.
                  disabled={
                    formFrozen ||
                    !hasManagedLeadRequestValuesEdits(initial, values)
                  }
                  loading={mutation.isPending}
                >
                  {mutation.isPending ? 'Salvataggio…' : 'SALVA'}
                </CtaButton>
              </Box>
            </Box>
          )}

          {failure && (
            <ManagedLeadFailureAlert
              {...failure}
              severity="error"
              onRetry={retry}
            />
          )}
        </Stack>
      </Box>
    </ModalContainer>
  );
};

export default ManagedLeadRequestValuesModal;
